import axios from "../../axios/AxiosInstance";
import { useEffect, useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { Modal } from "antd";

export const readNotes = (key) => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    return (Array.isArray(saved) ? saved : saved ? [saved] : []).filter(
      (note) =>
        note &&
        typeof note.text === "string" &&
        typeof note.author === "string" &&
        note.savedAt &&
        !Number.isNaN(new Date(note.savedAt).getTime()),
    );
  } catch {
    return [];
  }
};
export default function InvoiceFollowUp({ bill }) {
  const key = `invoice-follow-up:${localStorage.getItem("userId") || "unknown"}:${bill._id}`;
  const [notes, setNotes] = useState(bill.comments || []);
  const [legacyNotes,setLegacyNotes] = useState(()=>readNotes(key));
  const [existingNote,setExistingNote] = useState(bill.notesRelance || '');
  const [loading,setLoading] = useState(false);
  const [busy,setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    setNotes(bill.comments || []);
    setLegacyNotes(readNotes(key));
    setExistingNote(bill.notesRelance || "");
    setDraft("");
    setEditing(false);
    setError("");
  }, [key, bill.notesRelance, bill.comments]);
  const cancel = () => {
    setDraft("");
    setEditing(false);
    setError("");
  };
  const apply = data => {
    if(!data || !Array.isArray(data.comments))throw new Error('Invalid response');
    setNotes(data.comments);setExistingNote(data.notesRelance || '');
  };
  useEffect(()=>{
    if(!editing)return;
    let active=true;
    const load=async()=>{setLoading(true);try{const {data}=await axios.get(`facture/${bill._id}/comments`);if(active){apply(data);setError('');}}catch{if(active)setError('Impossible de charger le suivi partagé.');}finally{if(active)setLoading(false);}};
    load();window.addEventListener('focus',load);
    return()=>{active=false;window.removeEventListener('focus',load);};
  },[editing,bill._id]);
  const save = async event => {
    event.preventDefault();const text=draft.trim();if(!text||busy)return;
    setBusy(true);setError('');
    try {const {data}=await axios.post(`facture/${bill._id}/comment`,{text});apply(data);setDraft('');}
    catch {setError('Impossible d’enregistrer le commentaire partagé. Votre texte est conservé.');}
    finally {setBusy(false);}
  };
  const commentCount = notes.length + (existingNote ? 1 : 0);
  return (
    <div onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        onClick={() => {
          setLegacyNotes(readNotes(key));
          setDraft("");
          setError("");
          setEditing(true);
        }}
        aria-label="Ajouter un commentaire"
        title={commentCount ? `${commentCount} commentaire${commentCount > 1 ? "s" : ""} de suivi` : "Commentaires de la facture"}
        className="inline-flex items-center justify-center rounded-lg p-2 text-blue-700 hover:bg-blue-50"
      >
        <MessageSquare size={18} />
        {commentCount > 0 && <span aria-label={`${commentCount} commentaire${commentCount > 1 ? 's' : ''}`} className="ml-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">{commentCount}</span>}
      </button>
      <Modal
        open={editing}
        onCancel={cancel}
        footer={null}
        centered
        width={760}
        title={`Commentaires — Facture ${bill.numeroFacture || bill.IdFacture || "—"}${bill.client?.companyName ? ` · ${bill.client.companyName}` : ""}`}
      >
        <p className="mb-5 mt-2 text-sm leading-relaxed text-slate-500">
          Notes internes pour le suivi des impayés : relances, promesses de
          paiement, litiges et contacts.
        </p>
        <div className="max-h-[40vh] space-y-3 overflow-y-auto">
          {loading && <p role="status" className="text-sm text-slate-500">Chargement du suivi partagé…</p>}
          {[...notes].sort((a,b)=>new Date(b.date)-new Date(a.date)).map((note, index) => (
            <article
              key={note.id || `${note.date}-${index}`}
              className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3"
            >
              <div className="mb-1 flex items-center justify-between gap-3">
                <strong className="text-sm text-slate-700">
                  {note.author}
                </strong>
                <div className="flex shrink-0 items-center gap-2">
                  <time
                    dateTime={note.date}
                    className="text-xs text-slate-400"
                  >
                    {new Date(note.date).toLocaleString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" })}
                  </time>

                </div>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">
                {note.text}
              </p>
            </article>
          ))}
          {existingNote && (
            <article className="rounded-xl border border-slate-200 px-4 py-3">
              <p className="mb-1 text-xs text-slate-400">
                Commentaire existant de la facture
              </p>
              <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
                {existingNote}
              </p>
            </article>
          )}
          {!notes.length && !existingNote && (
            <p className="py-4 text-center text-sm text-slate-400">
              Aucun commentaire pour le moment.
            </p>
          )}
        </div>
        {legacyNotes.length>0 && <section className="mt-4 rounded-lg border border-amber-200 p-3"><h4 className="text-sm font-medium">Anciennes notes sur cet appareil · non partagées</h4>{legacyNotes.map((note,index)=><article key={note.id||index} className="mt-3 text-sm"><p className="text-xs text-slate-500">{note.author} · {new Date(note.savedAt).toLocaleString('fr-FR')}</p><p className="whitespace-pre-wrap">{note.text}</p><button type="button" disabled={busy} onClick={()=>setDraft(note.text)} className="mt-1 text-xs underline">Reprendre pour partager</button></article>)}</section>}
        <form
          onSubmit={save}
          className="mt-5 space-y-3 border-t border-slate-100 pt-4"
        >
          <textarea
            aria-label="Commentaire de suivi"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={1000}
            rows={2}
            required
            placeholder="Ajouter un commentaire (ex. : Relancé par téléphone, paiement promis le 15)"
            className="w-full resize-y rounded-lg border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          {error && (
            <p role="alert" className="text-xs text-red-700">
              {error}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              Partagés avec l’agence et le dashboard.
            </p>
            <button
              type="submit"
              disabled={!draft.trim() || busy || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-40"
            >
              <Send size={16} />
              Ajouter
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
